import { DatabaseAdapterInterface } from '@chem-po/core'
import {
  AnyObject,
  BaseQuery,
  BasicUseDocument,
  emptyBasicUseDocument,
  emptyUseQuery,
  emptyUseQueryCount,
  UseQuery,
  UseQueryCount,
} from '@hb/shared'
import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react'

export type BasicUseQuery<Data extends AnyObject> = Pick<
  UseQuery<Data>,
  'data' | 'loading' | 'error'
>

export type UseDocumentHook<Data extends AnyObject> = (
  collectionPath: string | null,
  id?: string | null,
) => BasicUseDocument<Data>

export type UseQueryHook<Data extends AnyObject> = (
  baseQuery: BaseQuery<Data> | null,
  onNewData?: (newData: Record<string, Data> | null) => void,
) => BasicUseQuery<Data>

export type UseQueryCountHook = (query: BaseQuery<AnyObject> | null) => UseQueryCount

// export type SubscribeTo

export interface FetchDataContextData {
  useDocument: <T extends AnyObject>(
    collectionPath: string | null,
    id?: string | null,
  ) => BasicUseDocument<T>
  useQuery: <T extends AnyObject>(
    baseQuery: BaseQuery<T> | null,
    onNewData?: (newData: Record<string, T> | null) => void,
  ) => BasicUseQuery<T>
  useQueryCount: <T extends AnyObject>(query: BaseQuery<T> | null) => UseQueryCount
  subscribeToItem: DatabaseAdapterInterface['subscribeToItem']
  subscribeToQuery: DatabaseAdapterInterface['subscribeToQuery']
  fetchItem: DatabaseAdapterInterface['fetchItem']
  fetchItems: DatabaseAdapterInterface['fetchItems']
  updateItem: DatabaseAdapterInterface['updateItem']
}
export const FetchDataContext = createContext<FetchDataContextData>({
  useDocument: () => emptyBasicUseDocument,
  useQuery: () => emptyUseQuery,
  useQueryCount: () => emptyUseQueryCount,
  subscribeToItem: () => () => {},
  subscribeToQuery: () => () => {},
  fetchItem: async () => null,
  fetchItems: async () => [],
  updateItem: async () => {},
})

export const FetchDataProvider = ({
  children,
  useDocument,
  useQuery,
  useQueryCount,
  subscribeToItem,
  subscribeToQuery,
  fetchItem,
  fetchItems,
  updateItem,
}: PropsWithChildren<FetchDataContextData>) => {
  const value = useMemo(
    () => ({
      useDocument,
      useQuery,
      useQueryCount,
      subscribeToItem,
      subscribeToQuery,
      fetchItem,
      fetchItems,
      updateItem,
    }),
    [
      useDocument,
      useQuery,
      useQueryCount,
      subscribeToItem,
      subscribeToQuery,
      fetchItem,
      fetchItems,
      updateItem,
    ],
  )
  return <FetchDataContext.Provider value={value}>{children}</FetchDataContext.Provider>
}

export const useFetchData = () => useContext(FetchDataContext)
